import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  consultationSettings,
  integrationCalls,
  type ConsultationKind,
  type ConsultationSetting,
  type ConsultationVersion,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DetailTabs } from '@/shared/ui/DetailLayout';
import { Field } from '@/shared/ui/Field';
import { HistoryTable } from '@/shared/ui/HistoryTab';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { AVISO_AUTOAPROVACAO, autoriaDe } from './autoaprovacao';
import { Trilha } from './Trilha';

/** Os dois botoes da tela do OnPoint, nesta ordem. */
const abas: { key: ConsultationKind; title: string }[] = [
  { key: 'pre_validacao', title: 'Consulta Pre-validacao' },
  { key: 'pj', title: 'Consulta PJ' },
];

const rotulosDaAcao: Record<string, string> = {
  'parametrizacao.consulta.create': 'Rascunho das regras aberto',
  'parametrizacao.consulta.update': 'Rascunho das regras alterado',
  'parametrizacao.consulta.approve': 'Versao das regras ativada',
  'parametrizacao.consulta.revoke': 'Versao das regras revogada',
  'parametrizacao.consulta.credential_update': 'Dados de acesso alterados',
  'parametrizacao.consulta.credential_delete': 'Dados de acesso removidos',
};

const rotulosDoCampo: Record<string, string> = {
  kind: 'Tipo de consulta',
  rules_enabled: 'Ativar regras',
  provider_key: 'Bureau',
  username: 'Usuario de acesso',
  secret_configured: 'Segredo cadastrado',
  secret_set_at: 'Segredo cadastrado em',
  secret_replaced: 'Segredo trocado',
  version: 'Versao',
  status: 'Situacao',
  notes: 'Observacao',
  justification: 'Justificativa',
  revocation_reason: 'Motivo da revogacao',
  based_on_version: 'Copiada da versao',
  setting_id: 'Identificador da versao',
};

function ativado(valor: boolean): string {
  return valor ? 'Ativado' : 'Desativado';
}

function resumoDaVersao(versao: ConsultationVersion): string {
  if (versao.status === 'ativa') {
    return `Versao ${versao.version}, ativada por ${versao.activated_label || 'sistema'} em ${formatDateTime(versao.activated_at)}.`;
  }
  return `Rascunho ${versao.version}, salvo por ${versao.updated_label || versao.created_label || 'sistema'}.`;
}

/** Secao "Dados de acesso as consultas" (PAR-143). O segredo nunca volta da API. */
function DadosDeAcesso({
  tipo,
  podeEditar,
}: {
  tipo: ConsultationSetting;
  podeEditar: boolean;
}) {
  const { can, hasFeature } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const credencial = tipo.credential;

  const [bureau, setBureau] = useState(credencial?.provider_key ?? '');
  const [usuario, setUsuario] = useState(credencial?.username ?? '');
  const [segredo, setSegredo] = useState('');
  const [removendo, setRemovendo] = useState(false);

  // O catalogo de bureaus e o do registro de integracoes (P-INT). Sem a leitura dele,
  // a lista mostra so o bureau gravado.
  const podeVerBureaus =
    can('integracoes.integracao.view') && hasFeature('administracao.integracoes');
  const bureaus = useQuery({
    queryKey: ['bureaus'],
    queryFn: ({ signal }) => integrationCalls.bureaus(signal),
    enabled: podeVerBureaus,
  });
  const opcoes = (bureaus.data ?? [])
    .filter((item) => item.in_catalog)
    .map((item) => ({ value: item.key, label: nomeDoProvedor(item.key, item.name) }));
  if (bureau !== '' && !opcoes.some((opcao) => opcao.value === bureau)) {
    opcoes.push({ value: bureau, label: nomeDoProvedor(bureau) });
  }

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['consultation-settings'] });
    void queryClient.invalidateQueries({ queryKey: ['consultation-history'] });
  }

  const salvar = useMutation({
    mutationFn: () =>
      consultationSettings.setCredential(tipo.kind, {
        provider_key: bureau,
        username: usuario,
        ...(segredo !== '' ? { secret: segredo } : {}),
      }),
    onSuccess: () => {
      toast.success('Dados de acesso salvos.');
      setSegredo('');
      invalidar();
    },
  });
  const remover = useMutation({
    mutationFn: () => consultationSettings.removeCredential(tipo.kind),
    onSuccess: () => {
      toast.success('Dados de acesso removidos.');
      setRemovendo(false);
      setBureau('');
      setUsuario('');
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setRemovendo(false);
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <Card>
      <CardHeader
        title="Dados de acesso as consultas"
        description="Bureau e credencial de acesso deste tipo de consulta."
      />
      <CardBody>
        <form onSubmit={enviar} className="flex flex-col gap-4">
          {salvar.error ? (
            <Alert tone="danger" title="Nao foi possivel salvar os dados de acesso">
              {salvar.error.message}
            </Alert>
          ) : null}
          {!tipo.vault_available ? (
            <Alert tone="warning" title="Segredo indisponivel">
              O segredo de acesso nao pode ser guardado: a chave de segredos da
              aplicacao nao esta configurada. O bureau e o usuario podem ser salvos.
            </Alert>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-3">
            <Select
              label="Bureau"
              placeholder="Nenhum bureau"
              value={bureau}
              disabled={!podeEditar}
              options={opcoes}
              onChange={(evento) => setBureau(evento.target.value)}
            />
            <Field
              label="Usuario de acesso"
              maxLength={200}
              autoComplete="off"
              disabled={!podeEditar}
              value={usuario}
              onChange={(evento) => setUsuario(evento.target.value)}
            />
            <Field
              label="Segredo de acesso"
              type="password"
              autoComplete="new-password"
              maxLength={4096}
              disabled={!podeEditar || !tipo.vault_available}
              placeholder={
                credencial?.secret_configured
                  ? 'Cadastrado. Deixe em branco para manter.'
                  : 'Nao cadastrado'
              }
              value={segredo}
              onChange={(evento) => setSegredo(evento.target.value)}
            />
          </div>
          {credencial ? (
            <p className="text-2xs text-ink-500">
              {credencial.secret_configured
                ? `Segredo cadastrado em ${formatDateTime(credencial.secret_set_at)}. `
                : 'Sem segredo cadastrado. '}
              Ultima alteracao por {credencial.updated_label || 'sistema'} em{' '}
              {formatDateTime(credencial.updated_at)}.
            </p>
          ) : null}
          {podeEditar ? (
            <div className="flex flex-wrap justify-end gap-2">
              {credencial ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setRemovendo(true)}
                >
                  Remover dados de acesso
                </Button>
              ) : null}
              <Button type="submit" loading={salvar.isPending}>
                Salvar dados de acesso
              </Button>
            </div>
          ) : null}
        </form>
      </CardBody>
      <ConfirmDialog
        open={removendo}
        onClose={() => setRemovendo(false)}
        onConfirm={() => remover.mutate()}
        title="Remover dados de acesso"
        message="O bureau, o usuario e o segredo deste tipo de consulta deixam de existir. A remocao fica no historico."
        confirmLabel="Remover"
        tone="danger"
        loading={remover.isPending}
      />
    </Card>
  );
}

/**
 * Secao "Ativar regras" (PAR-144): o interruptor do OnPoint como parametro
 * versionado, com rascunho e ativacao por outra pessoa.
 */
function AtivarRegras({
  tipo,
  podeEditar,
  podeAprovar,
}: {
  tipo: ConsultationSetting;
  podeEditar: boolean;
  podeAprovar: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const base = tipo.draft_setting ?? tipo.active_setting;
  const [regras, setRegras] = useState(base?.values.rules_enabled ?? false);
  const [observacao, setObservacao] = useState(tipo.draft_setting?.notes ?? '');
  const [ativando, setAtivando] = useState(false);
  const [justificativa, setJustificativa] = useState('');

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['consultation-settings'] });
    void queryClient.invalidateQueries({ queryKey: ['consultation-history'] });
  }

  const salvar = useMutation({
    mutationFn: () =>
      consultationSettings.saveDraft(tipo.kind, {
        rules_enabled: regras,
        notes: observacao,
      }),
    onSuccess: (versao) => {
      toast.success(`Rascunho ${versao.version} salvo.`);
      invalidar();
    },
  });
  const ativar = useMutation({
    mutationFn: () => consultationSettings.activate(tipo.kind, justificativa),
    onSuccess: (versao) => {
      toast.success(`Versao ${versao.version} em vigor.`);
      setAtivando(false);
      setJustificativa('');
      invalidar();
    },
  });

  const rascunho = tipo.draft_setting;
  const autoria = autoriaDe(rascunho?.authored_by_you, rascunho?.may_self_approve);

  return (
    <Card>
      <CardHeader
        title="Ativar regras"
        description="Liga as regras deste tipo de consulta, com aprovacao de outra pessoa."
        action={
          tipo.active_setting ? (
            <Badge
              tone={tipo.active_setting.values.rules_enabled ? 'success' : 'neutral'}
            >
              {ativado(tipo.active_setting.values.rules_enabled)}
            </Badge>
          ) : (
            <Badge tone="neutral">Desativado</Badge>
          )
        }
      />
      <CardBody className="flex flex-col gap-4">
        <Alert tone="info" title="Parametro guardado, sem efeito">
          O valor fica gravado com versao e aprovacao. Nenhuma consulta passa a ser
          feita ou barrada por ele ate a homologacao das regras (PAR-144).
        </Alert>
        <p className="text-xs text-ink-700">
          Em vigor:{' '}
          {tipo.active_setting
            ? `${ativado(tipo.active_setting.values.rules_enabled)}. ${resumoDaVersao(tipo.active_setting)}`
            : 'nenhuma versao ainda (Desativado).'}
        </p>
        {rascunho ? (
          <p className="text-xs text-ink-700">
            Rascunho: {ativado(rascunho.values.rules_enabled)}.{' '}
            {resumoDaVersao(rascunho)}
          </p>
        ) : null}

        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar o rascunho">
            {salvar.error.message}
          </Alert>
        ) : null}

        {podeEditar ? (
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-brand-500)]"
                checked={regras}
                onChange={(evento) => setRegras(evento.target.checked)}
              />
              Regras ativadas neste tipo de consulta
            </label>
            <Textarea
              label="Observacao da versao"
              maxLength={4000}
              value={observacao}
              onChange={(evento) => setObservacao(evento.target.value)}
            />
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {rascunho && podeAprovar && autoria.barrada ? (
            <p className="text-2xs text-ink-500">
              Quem criou ou editou o rascunho nao o ativa. Peca a outra pessoa com a
              alcada de aprovacao.
            </p>
          ) : null}
          {rascunho && podeAprovar && autoria.autoaprovacao ? (
            <p className="text-2xs text-ink-500">{AVISO_AUTOAPROVACAO}</p>
          ) : null}
          {podeEditar ? (
            <Button
              variant="secondary"
              loading={salvar.isPending}
              onClick={() => salvar.mutate()}
            >
              Salvar rascunho
            </Button>
          ) : null}
          {rascunho && podeAprovar ? (
            <Button disabled={autoria.barrada} onClick={() => setAtivando(true)}>
              Ativar versao {rascunho.version}
            </Button>
          ) : null}
        </div>
      </CardBody>

      <Modal
        open={ativando}
        onClose={() => setAtivando(false)}
        title="Ativar versao das regras"
        description="A versao em vigor passa a ser revogada, com o motivo da substituicao."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAtivando(false)}>
              Cancelar
            </Button>
            <Button
              disabled={justificativa.trim() === ''}
              loading={ativar.isPending}
              onClick={() => ativar.mutate()}
            >
              Ativar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {ativar.error ? (
            <Alert tone="danger" title="Nao foi possivel ativar">
              {ativar.error.message}
            </Alert>
          ) : null}
          <Textarea
            label="Justificativa da ativacao"
            required
            maxLength={4000}
            value={justificativa}
            onChange={(evento) => setJustificativa(evento.target.value)}
          />
        </div>
      </Modal>
    </Card>
  );
}

/** "Historico de alteracoes" da tela, com o antes e o depois de cada mudanca. */
function Historico({ onClose }: { onClose: () => void }) {
  const historico = useQuery({
    queryKey: ['consultation-history'],
    queryFn: ({ signal }) => consultationSettings.history(signal),
  });
  return (
    <Modal open onClose={onClose} title="Historico de alteracoes" size="lg">
      {historico.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : historico.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o historico">
          {historico.error.message}
        </Alert>
      ) : (
        <HistoryTable
          rows={historico.data}
          emptyText="Nenhuma alteracao registrada"
          actionLabel={(acao) => rotulosDaAcao[acao] ?? acao}
          fieldLabel={(campo) => rotulosDoCampo[campo] ?? campo}
        />
      )}
    </Modal>
  );
}

/**
 * Consultas PJ e Pre-validacao (`/parametrizacao/consulta-pj`), a tela do OnPoint com
 * os botoes Consulta Pre-validacao e Consulta PJ, as secoes "Dados de acesso as
 * consultas" e "Ativar regras" e o "Historico de alteracoes".
 */
export function ConsultationPage() {
  const { can, hasFeature } = useAuth();
  const podeLer = can('parametrizacao.consulta.view');
  const podeEditar = can('parametrizacao.consulta.update');
  const podeAprovar = can('parametrizacao.consulta.approve');
  const [aba, setAba] = useState<ConsultationKind>('pre_validacao');
  const [vendoHistorico, setVendoHistorico] = useState(false);

  const consulta = useQuery({
    queryKey: ['consultation-settings'],
    queryFn: ({ signal }) => consultationSettings.list(signal),
    enabled: podeLer,
  });

  const podeVerBureaus =
    can('integracoes.integracao.view') && hasFeature('administracao.integracoes');

  const cabecalho = (
    <>
      <Trilha
        itens={[{ rotulo: 'Parametrizacao', para: '/parametrizacao' }]}
        atual="Consultas PJ e Pre-validacao"
      />
      <PageHeader
        title="Consultas PJ e Pre-validacao"
        description="Dados de acesso as consultas e as regras da consulta PJ e da pre-validacao."
        actions={
          podeLer ? (
            <>
              {podeVerBureaus ? (
                <Link
                  to="/parametrizacao/bureaus-consulta"
                  className="text-xs font-medium text-brand-700 hover:underline"
                >
                  Bureaus por tipo de consulta
                </Link>
              ) : null}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVendoHistorico(true)}
              >
                Historico de alteracoes
              </Button>
            </>
          ) : undefined
        }
      />
    </>
  );

  if (!podeLer) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Alert tone="info" title="Sem acesso">
          O seu acesso nao inclui a parametrizacao das consultas
          (parametrizacao.consulta.view).
        </Alert>
      </div>
    );
  }
  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (consulta.isError) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Alert tone="danger" title="Nao foi possivel carregar as consultas">
          {consulta.error.message}
        </Alert>
      </div>
    );
  }

  const tipo = consulta.data.find((item) => item.kind === aba);

  return (
    <div className="flex flex-col gap-4">
      {cabecalho}
      <DetailTabs
        tabs={abas}
        active={aba}
        onChange={setAba}
        label="Tipos de consulta"
      />
      {tipo ? (
        // A chave remonta as secoes quando o tipo ou a versao muda, para que os campos
        // partam do que esta gravado.
        <div
          key={`${tipo.kind}-${tipo.draft_setting?.updated_at ?? ''}-${tipo.active_setting?.id ?? ''}-${tipo.credential?.updated_at ?? ''}`}
          className="flex flex-col gap-4"
        >
          <DadosDeAcesso tipo={tipo} podeEditar={podeEditar} />
          <AtivarRegras tipo={tipo} podeEditar={podeEditar} podeAprovar={podeAprovar} />
        </div>
      ) : null}
      {vendoHistorico ? <Historico onClose={() => setVendoHistorico(false)} /> : null}
    </div>
  );
}
